import React, { useEffect, useState } from 'react';
import { Calendar } from 'primereact/calendar';
import { useAuth } from '../context/AuthContext';
import { timeTrackingService, MyUsageStats } from '../services/timeTrackingService';
import { UserRole } from '../types';

const STATUS_LABELS: Record<string, string> = {
  PENDING_APPROVAL: 'Pending approval',
  REJECTED: 'Rejected',
  NEW: 'New',
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  PENDING: 'Pending',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
  CANCELLED: 'Cancelled'
};

const formatHours = (hours: number) =>
  `${hours.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} h`;

const formatCurrency = (amount: number, currency = 'EUR') => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

const ProfileUsageStats: React.FC = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [stats, setStats] = useState<MyUsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isAgentLike = user?.role === UserRole.AGENT || user?.role === UserRole.SUPER_ADMIN;
  const monthLabel = selectedDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await timeTrackingService.getMyUsageStats({
          month: selectedDate.getMonth() + 1,
          year: selectedDate.getFullYear()
        });
        if (!cancelled) {
          setStats(data);
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err.response?.data?.message || 'Could not load ticket stats');
          setStats(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  const hoursValue = isAgentLike
    ? (stats?.hoursLogged ?? 0)
    : (stats?.hoursOnMyTickets ?? 0);
  const hoursHint = isAgentLike
    ? 'Time you tracked this month'
    : 'Billable time logged on tickets you opened';

  const statusEntries = Object.entries(stats?.byStatus || {}).filter(([, count]) => count > 0);

  return (
    <section className="profile-panel usage-panel">
      <div className="profile-panel-head usage-panel-head">
        <div className="profile-panel-icon"><i className="pi pi-chart-bar"></i></div>
        <div>
          <h2>Ticket stats</h2>
          <p className="profile-hint">Tickets you opened and hours used in {monthLabel}.</p>
        </div>
        <div className="usage-month-picker">
          <i className="pi pi-calendar"></i>
          <Calendar
            value={selectedDate}
            onChange={(e) => e.value && setSelectedDate(e.value as Date)}
            view="month"
            dateFormat="MM yy"
            showIcon={false}
          />
        </div>
      </div>

      {error && <div className="profile-error">{error}</div>}

      <div className="usage-stat-grid">
        <div className="usage-stat-card">
          <span className="usage-stat-label">Tickets opened</span>
          <strong>{loading ? '—' : stats?.ticketsOpened ?? 0}</strong>
        </div>
        <div className="usage-stat-card">
          <span className="usage-stat-label">Hours spent</span>
          <strong>{loading ? '—' : formatHours(hoursValue)}</strong>
          <span className="usage-stat-hint">{hoursHint}</span>
        </div>
        {!isAgentLike && (
          <div className="usage-stat-card">
            <span className="usage-stat-label">Estimated cost</span>
            <strong>
              {loading
                ? '—'
                : stats?.cost == null
                  ? 'Rate not set'
                  : formatCurrency(stats.cost, stats.currency)}
            </strong>
            <span className="usage-stat-hint">
              {stats?.hourlyRate
                ? `At ${formatCurrency(stats.hourlyRate, stats.currency)} / h`
                : 'Set an hourly rate on the company to see cost'}
            </span>
          </div>
        )}
      </div>

      {!loading && statusEntries.length > 0 && (
        <div className="usage-status-row">
          {statusEntries.map(([status, count]) => (
            <span key={status} className="usage-status-chip">
              {STATUS_LABELS[status] || status.replace(/_/g, ' ')}
              <b>{count}</b>
            </span>
          ))}
        </div>
      )}
    </section>
  );
};

export default ProfileUsageStats;
